"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZoomRenderer = void 0;
var tslib_1 = require("tslib");
var core_api_1 = require("@ikegps/core-api");
var mobx_1 = require("mobx");
var vector_1 = require("../../../math/vector");
var model_1 = require("../../../model");
var unit_1 = require("../../../unit/unit");
var util_draw_svg_1 = require("../../drawable/util.draw.svg");
var mithril_1 = require("../../mithril");
var zoom_style_1 = require("./zoom.style");
var ZoomRenderer = /** @class */ (function () {
    function ZoomRenderer(ctx) {
        this.zoomLevel = 1;
        this.width = 0;
        this.height = 0;
        this.useCrosshair = true;
        this.useWireCalipers = true;
        this.crosshairCenterGap = 4;
        this.width = ctx.size.width || 200;
        this.height = ctx.size.height || 200;
        this.zoomLevel = ctx.zoomLevel || 1;
        this.useCrosshair = ctx.useCrosshair == null ? true : ctx.useCrosshair;
    }
    Object.defineProperty(ZoomRenderer.prototype, "isShown", {
        get: function () {
            if (this.dc == null) {
                return false;
            }
            var current = this.dc.state.selectedId;
            if (current == null) {
                return false;
            }
            return true;
        },
        enumerable: false,
        configurable: true
    });
    ZoomRenderer.prototype.calculateCoordinate = function (offset, which) {
        var max = this.dc.capture.resolution[which];
        var min = 0;
        var calculatedOffset = Math.max(min, offset);
        calculatedOffset = Math.min(max, calculatedOffset);
        calculatedOffset = (calculatedOffset * this.zoomLevel) - (this[which] / 2);
        return -calculatedOffset;
    };
    ZoomRenderer.prototype.getZoomDrawable = function (dc) {
        var selected = dc.selected;
        if (selected == null) {
            return null;
        }
        if (model_1.CollectionModelDrawableLine.isLine(selected)) {
            if (dc.state.selectedRegion === util_draw_svg_1.IkeDrawLabel.LABEL) {
                return selected.start;
            }
        }
        if (selected instanceof model_1.CollectionModelDrawableLabel && selected.parent instanceof model_1.CollectionModelDrawableVerticalSeparation) {
            return null;
        }
        return selected;
    };
    ZoomRenderer.prototype.getPixelOffset = function (wireSizeInches) {
        var wireSize = unit_1.IkeUnit.convert(wireSizeInches, unit_1.IkeUnit.METER, unit_1.IkeUnit.INCH, core_api_1.IkeUnitType.DISTANCE); //inches to meters
        var moveUpXY = new vector_1.Vector(0.0, (-wireSize / 2.0)); // in meters :zero in width, up 1/2 selected wire;
        var moveDownXY = new vector_1.Vector(0.0, (wireSize / 2.0)); // in meters :zero in width, down 1/2 selected wire;
        return { moveUpXY: moveUpXY, moveDownXY: moveDownXY };
    };
    ZoomRenderer.prototype.getWirePx = function (wireSizeInches, drawMarkerX, drawMarkerY, geoDraw) {
        //the full size picture coordinates of the center of the zoom window
        var drawXY = new vector_1.Vector(drawMarkerX, drawMarkerY);
        var halfHeight = this.height / 2.0;
        var halfWidth = this.width / 2.0;
        //gets the moved pixel value of the full picture
        var geoMoveUp = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveUpXY);
        var geoMoveDown = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveDownXY);
        //calculates the equivalency in the zoom window
        var MoveUp = new vector_1.Vector(halfWidth, halfHeight - (this.zoomLevel * Math.abs(drawMarkerY - geoMoveUp.y)));
        var MoveDown = new vector_1.Vector(halfWidth, halfHeight + (this.zoomLevel * Math.abs(drawMarkerY - geoMoveDown.y)));
        return { MoveUp: MoveUp, MoveDown: MoveDown };
    };
    ZoomRenderer.prototype.view = function (dc) {
        this.dc = dc;
        if (this.isShown === false) {
            return;
        }
        var drawable = this.getZoomDrawable(dc);
        if (drawable == null) {
            return;
        }
        this.useWireCalipers = this.dc.state.showCablemeasure;
        var offsetX = drawable.x;
        var offsetY = drawable.y;
        var capture = this.dc.capture;
        var src = capture.file.url;
        var resolution = capture.resolution;
        var theImage = (0, mithril_1.m)('img', {
            src: src,
            className: zoom_style_1.CSS.image,
            style: "transform: scale(".concat(this.zoomLevel, "); ") +
                "filter: url(#".concat(dc.id, ":brightness); ") +
                "left: ".concat(this.calculateCoordinate(offsetX, 'width'), "px; ") +
                "top: ".concat(this.calculateCoordinate(offsetY, 'height'), "px;"),
            width: resolution.width,
            height: resolution.height
        });
        var containerChildren = [theImage];
        if (this.useCrosshair) {
            var halfHeight = this.height / 2;
            var halfWidth = this.width / 2;
            var crosshairElements = [];
            if (this.useWireCalipers) {
                // Left to center
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth - (halfWidth / 16),
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth - (halfWidth / 16),
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                // Right to center
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: this.width - (halfWidth / 16), //halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: this.width - (halfWidth / 16), //halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: halfWidth + (halfWidth / 16),
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: halfWidth + (halfWidth / 16),
                    y2: halfHeight
                }));
            }
            else {
                // Left to right
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight
                }));
                // Right to left
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight
                }));
            }
            // Top to bottom
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: 0,
                x2: halfWidth,
                y2: halfHeight - this.crosshairCenterGap
            }));
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.INNERLINE_STROKE,
                'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: 0,
                x2: halfWidth,
                y2: halfHeight - this.crosshairCenterGap
            }));
            // Bottom to top
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: halfHeight + this.crosshairCenterGap,
                x2: halfWidth,
                y2: this.height
            }));
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.INNERLINE_STROKE,
                'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: halfHeight + this.crosshairCenterGap,
                x2: halfWidth,
                y2: this.height
            }));
            var crosshairSVG = (0, mithril_1.m)('svg', {
                className: zoom_style_1.CSS.crosshair,
                width: '100%',
                height: '100%',
                viewBox: '0 0 ' + this.width + ' ' + this.height
            }, crosshairElements);
            containerChildren.push(crosshairSVG);
        }
        //VERSION 3
        if (this.useWireCalipers) {
            var center_x = this.width / 2;
            var caliperElements = [];
            var caliperLoc = this.getWirePx(dc.state.cablemeasure, drawable.x, drawable.y, dc.drawState.collection.state.getGeometry(drawable.captureId));
            var moveUp = caliperLoc.MoveUp;
            var moveDown = caliperLoc.MoveDown;
            //upper caliper
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveUp.y,
                x2: center_x - (center_x / 16),
                y2: moveUp.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveUp.y,
                x2: center_x - (center_x / 16),
                y2: moveUp.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x + (center_x / 16),
                y1: moveUp.y,
                x2: this.width - (center_x / 16),
                y2: moveUp.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x + center_x / 16,
                y1: moveUp.y,
                x2: this.width - (center_x / 16),
                y2: moveUp.y
            }));
            //lower caliper
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveDown.y,
                x2: center_x - (center_x / 16),
                y2: moveDown.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveDown.y,
                x2: center_x - (center_x / 16),
                y2: moveDown.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x + (center_x / 16),
                y1: moveDown.y,
                x2: this.width - (center_x / 16),
                y2: moveDown.y
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x + (center_x / 16),
                y1: moveDown.y,
                x2: this.width - (center_x / 16),
                y2: moveDown.y
            }));
            caliperElements.push((0, mithril_1.m)('text', {
                className: zoom_style_1.CSS.crosshairCaliper,
                fill: 'white',
                stroke: 'black',
                x: center_x / 2,
                y: moveUp.y - 15,
                'text-anchor': 'middle',
                'font-size': 20,
                'alignment-baseline': 'middle',
                style: {
                    'user-select': 'none',
                    'pointer-events': 'none',
                    'font-weight': 'bold'
                }
            }, this.dc.state.cablemeasure + ' \'\' '));
            var crosshairSVG = (0, mithril_1.m)('svg', {
                className: zoom_style_1.CSS.crosshair,
                width: '100%',
                height: '100%',
                viewBox: '0 0 ' + this.width + ' ' + this.height
            }, caliperElements);
            containerChildren.push(crosshairSVG);
        }
        var theContainer = (0, mithril_1.m)('div', {
            className: zoom_style_1.CSS.container,
            style: 'width: ' + this.width + 'px; height: ' + this.height + 'px; overflow: hidden;'
        }, containerChildren);
        return theContainer;
    };
    ZoomRenderer.CSS = zoom_style_1.CSS;
    ZoomRenderer.Style = zoom_style_1.Style;
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "zoomLevel", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "width", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "height", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "useCrosshair", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "useWireCalipers", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], ZoomRenderer.prototype, "crosshairCenterGap", void 0);
    return ZoomRenderer;
}());
exports.ZoomRenderer = ZoomRenderer;
//# sourceMappingURL=zoom.render.js.map